"use client";

import { type PropsWithChildren, useMemo } from "react";
import { ConfigProvider } from "antd";
import { StyleProvider } from "@ant-design/cssinjs";
import { ruRU } from "../theme/locale";
import { BRAND, componentTheme, RADIUS, TYPOGRAPHY } from "../theme/tokens";
import { light, dark, compact } from "../theme/algorithms";

type Mode = "light" | "dark";
type Density = "normal" | "compact";

export type AppThemeProviderProps = PropsWithChildren<{
    mode?: Mode;
    density?: Density;
    primaryColor?: string;
    locale?: typeof ruRU;
}>;

export function AppThemeProvider({
    children,
    mode = "light",
    density = "normal",
    primaryColor = BRAND.primary,
    locale = ruRU,
}: AppThemeProviderProps) {
    const algorithm = useMemo(() => {
        const base = mode === "dark" ? dark : light;
        return density === "compact" ? compact : base;
    }, [mode, density]);

    return (
        <StyleProvider hashPriority="high">
            <ConfigProvider
                locale={locale}
                theme={{
                    algorithm,
                    token: {
                        colorPrimary: primaryColor,
                        fontFamily: TYPOGRAPHY.fontFamily,
                        borderRadius: RADIUS,
                        colorBorderSecondary: BRAND.gray200,
                    },
                    components: componentTheme,
                }}
                renderEmpty={() => null}
            >
                {children}
            </ConfigProvider>
        </StyleProvider>
    );
}
